import { IGradingTemplateSurveyDetail } from '@lb/api/core/interfaces/igrading-template.interface';
import { ISurveyResponseSummary } from '@lb/api/mylb/interfaces';

/**
 * Where a survey's response is stored — mirrors Lesson.SemesterLessonTypes.ResponseScope, which the API
 * derives from the survey's trigger. Never chosen by the page.
 */
export enum SurveyResponseScope {
	/** Once per enrollment (Enrollment Start): no semester, no lesson. */
	Enrollment = 1,
	/** Once per semester (First Lesson, Mid Semester, Final Lesson): semester only. */
	Semester = 2,
	/** Once per lesson (Standard): the lesson. */
	Lesson = 3
}

/**
 * The response that answers `survey` for `lesson`, or null.
 *
 * Survey definitions are shared across semesters (same surveyID on the grading template), so a
 * response only counts when it was given for the same scope. Semester milestones move between lessons
 * on every resequence, so First / Mid / Final responses match on the lesson's semester, not its ID — a
 * mid-semester survey answered before a reschedule still reads as answered on whichever lesson holds
 * the midpoint now.
 *
 * `responseScope` is optional on the survey because a frontend deploy can lead the API; it falls back
 * to Semester, which is what every survey on a lesson page is today.
 */
export function findSurveyResponse(
	responses: readonly ISurveyResponseSummary[],
	survey: Pick<IGradingTemplateSurveyDetail, 'surveyID' | 'responseScope'>,
	lesson: { lessonID: number; semesterID?: number | null }
): ISurveyResponseSummary | null {
	const scope = survey.responseScope ?? SurveyResponseScope.Semester;

	return (
		responses.find((r) => {
			if (r.surveyID !== survey.surveyID) return false;

			switch (scope) {
				case SurveyResponseScope.Enrollment:
					return r.semesterID == null && r.lessonID == null;
				case SurveyResponseScope.Lesson:
					return r.lessonID === lesson.lessonID;
				default:
					return lesson.semesterID != null && r.lessonID == null && r.semesterID === lesson.semesterID;
			}
		}) ?? null
	);
}

/** Whether two responses occupy the same key — used to replace a summary in place after a submit. */
export function isSameSurveyResponseKey(a: ISurveyResponseSummary, b: ISurveyResponseSummary): boolean {
	return a.surveyID === b.surveyID && (a.semesterID ?? null) === (b.semesterID ?? null) && (a.lessonID ?? null) === (b.lessonID ?? null);
}
